import { Injectable } from "@angular/core"
import { DynamicVariableResponse } from "@api/communication/models/communication"
import { Arithmetic, NodeFunctionBody } from "@api/communication/models/functions"
import { LabelValue } from "@shared/models/LabelValue"
import { FlowNodeService } from "app/communication/automation/flow-node.service"
import { ConditionSet, filterFunctions, MatchMode, VisibilityMode, WeekDayName } from "./ag-conditional-dialog.component"

const NEGATION_FUNCTION_TYPE = "not"
const RESERVATION_STATUS_VALUE = "Reservation:Status"
const NUMBER_OF_NIGHTS = "NumberofNights"

// an equality rule only compares a single value, so a condition holding several has to be
// saved as a list membership check instead. Looked up by display name like the numeric
// rules, with several wordings accepted because the backend's naming has varied.
const IN_LIST_RULES: string[] = ["is in list", "in list", "list match any", "list contains any"]

// a numeric condition is stored as a min/max range but saved as up to two separate
// comparisons against the same dynamic variable, so the backend rules for ">=" and "<="
// are looked up by display name. Several wordings are accepted because the backend's
// display names have varied; first match wins.
const GREATER_OR_EQUAL_RULES: string[] = ["is equal to or greater than", "is greater than or equal to", "greater than or equal", "is greater than or equal"]
const LESS_OR_EQUAL_RULES: string[] = ["is equal to or less than", "is less than or equal to", "less than or equal", "is less than or equal"]

// the status card has no comparer control — every status condition is an equality check
const DEFAULT_STATUS_COMPARER: LabelValue = { label: "is equal to", value: "==" }

const WEEK_DAY_LABELS: Record<string, WeekDayName> = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
}

// index order the backend expects for withinTimeSpan's day argument
const WEEK_DAY_ORDER: string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// the catalogue the codec needs from the dialog: the backend rule list, plus the
// ReservationData properties used to resolve a condition's dynamic-variable path
export type CodecContext = {
  functionList: Arithmetic[]
  reservationData?: DynamicVariableResponse
}

// Serialises the dialog's condition sets into the backend's NodeFunctionBody tree, and
// reads the numeric/negation structure back out. Kept free of dialog state so the
// round trip can be unit tested on its own.
@Injectable({ providedIn: "root" })
export class ConditionBodyCodecService {
  private functionList: Arithmetic[] = []
  private ReservationData?: DynamicVariableResponse
  private weekDays = WEEK_DAY_ORDER

  constructor(private flowNode: FlowNodeService) {}

  public use(context: CodecContext): void {
    this.functionList = context.functionList ?? []
    this.ReservationData = context.reservationData
  }

  // ===== Serialising =====
  // the whole body - exception set included - is wrapped in a single negate for "Hide if",
  // rather than rebuilt with inverted rules
  public build(mainSet: ConditionSet, exceptionSet: ConditionSet | null, mode: VisibilityMode): NodeFunctionBody {
    const body = this.buildConditionBody(mainSet, exceptionSet)
    if (mode !== "Hide") return body
    return this.negateBody(body) ?? body
  }

  private buildConditionBody(mainSet: ConditionSet, exceptionSet: ConditionSet | null): NodeFunctionBody {
    const mainBody = this.buildSetBody(mainSet)
    const exceptionBody = exceptionSet && exceptionSet.filterList.length > 0 ? this.buildSetBody(exceptionSet) : null
    if (!exceptionBody) return mainBody

    const negated = this.negateBody(exceptionBody)
    if (!negated) return mainBody

    const combined = this.flowNode.getNodeFunctionBody([], null, "f", "xAND")
    combined.ChildBodies.push(mainBody, negated)
    return combined
  }

  public buildSetBody(set: ConditionSet): NodeFunctionBody {
    const functionList = this.collectSetFunctions(set)
    const body = this.flowNode.getNodeFunctionBody([], null, "f", this.combinatorFor(set.matchMode))
    functionList.forEach((func) => body.ChildBodies.push(func))
    return body
  }

  private combinatorFor(matchMode: MatchMode): string {
    return matchMode === "any" ? "xOR" : "xAND"
  }

  // ===== Reading structure back =====
  // a "Hide if" body is the whole condition wrapped in a single negate
  public unwrapNegation(body: any): any | null {
    if (body?.BodyData !== NEGATION_FUNCTION_TYPE || body.ChildBodies?.length !== 1) return null
    return body.ChildBodies[0]
  }

  // a numeric bound saved on its own is a plain comparison, so the rule decides whether
  // its value belongs in the min slot or the max slot
  public numericSlotFor(functionType: any): 0 | 1 {
    const rule = this.functionList.find((item) => item.functionType === functionType)
    const name = rule?.displayName?.toLowerCase() ?? ""
    return LESS_OR_EQUAL_RULES.includes(name) ? 1 : 0
  }

  public isNumericFilter(filter: filterFunctions): boolean {
    return filter.infofilter.value === "Int32" || filter.infofilter.value === "Double" || filter.infofilter.value === "Decimal"
  }

  public numericBoundValue(filter: filterFunctions, slot: 0 | 1): string | null {
    return this.numericBound(filter, slot)
  }

  private dayNameFromLabel(label: string): WeekDayName {
    return WEEK_DAY_LABELS[label]
  }

  public collectSetFunctions(set: ConditionSet): NodeFunctionBody[] {
    var functionList: NodeFunctionBody[] = []
    set.filterList.forEach((filter) => {
      if (filter.infofilter.value === "StartDate" || filter.infofilter.value === "EndDate") {
        var BeforeAfterFunction = this.DateTimeCheck(filter)
        functionList.push(BeforeAfterFunction)
      } else if (filter.infofilter.dynamicVariable && filter.infofilter.value !== RESERVATION_STATUS_VALUE) {
        if (this.isNumericFilter(filter)) {
          functionList.push(this.numericRangeBody(filter))
        } else {
          var rulesFunction = this.dynamicFunctionBody(filter)
          functionList.push(rulesFunction)
        }
      } else if (filter.infofilter.value === "StartDateTime" || filter.infofilter.value === "EndDateTime") {
        var staticDateFunction = this.staticDateCheck(filter)
        if (staticDateFunction !== null) {
          functionList.push(staticDateFunction)
        }
      } else if (filter.infofilter.value === "WeekTimeSpan") {
        var weekTimeSpanFunc = this.weekTimeSpanFunction(filter)
        if (weekTimeSpanFunc !== null) {
          functionList.push(weekTimeSpanFunc)
        }
      } else {
        var EqualFunction = this.EqualStatusCheck(filter)
        functionList.push(EqualFunction)
      }
    })
    return functionList.filter((func) => func !== null && func !== undefined)
  }

  private negationFunctionType(): string | null {
    const negation = this.functionList.find((item) => item.functionType === NEGATION_FUNCTION_TYPE) ?? this.functionList.find((item) => item.returnType === "Boolean" && item.parameters?.length === 1 && item.parameters[0].type === "Boolean")
    return negation?.functionType ?? null
  }

  private negateBody(body: NodeFunctionBody): NodeFunctionBody | null {
    const functionType = this.negationFunctionType()
    if (!functionType) return null
    const negated = this.flowNode.getNodeFunctionBody([], null, "f", functionType)
    negated.ChildBodies.push(body)
    return negated
  }

  dynamicFunctionBody(filter: filterFunctions): NodeFunctionBody {
    const listRule = this.multiValueListRule(filter)
    var funcBody = this.flowNode.getNodeFunctionBody([], null, "f", listRule?.functionType ?? filter.usedFunction?.functionType)
    const bodyData = this.dynamicVariablePath(filter)
    if (!bodyData) return funcBody
    funcBody.ChildBodies.push(this.flowNode.getDynamicVariable(bodyData))

    if (listRule) {
      funcBody.ChildBodies.push(this.flowNode.getStaticVariable("IList<String>", this.filledValues(filter)))
      return funcBody
    }

    filter.svList.forEach((type) => {
      const value = type === "IList<String>" ? filter.svValues : filter.svValues.join("")
      funcBody.ChildBodies.push(this.flowNode.getStaticVariable(type, value))
    })
    return funcBody
  }

  private multiValueListRule(filter: filterFunctions): Arithmetic | undefined {
    if (this.filledValues(filter).length < 2) return undefined
    if (filter.svList?.includes("IList<String>")) return undefined
    return this.ruleByDisplayName(IN_LIST_RULES)
  }

  private filledValues(filter: filterFunctions): string[] {
    return (filter.svValues ?? []).filter((value) => !!value)
  }

  private dynamicVariablePath(filter: filterFunctions): string | null {
    if (filter.infofilter.fieldName === NUMBER_OF_NIGHTS) return NUMBER_OF_NIGHTS
    if (filter.infofilter.pmsVariable) return "Reservation:PMSVariables:" + filter.infofilter.label.replaceAll(" ", "_")

    const name = filter.infofilter.fieldName ?? this.ReservationData?.properties.find((x) => x.displayName === filter.infofilter.label)?.name
    if (!name) return null
    return name.startsWith("Reservation:") ? name : "Reservation:" + name
  }

  // the numeric card is a range, not a single comparison: min saves as ">=" and max as "<=".
  // Both bounds together become an AND of the two comparisons; a min on its own is emitted
  // as a single comparison so nothing redundant is stored.
  private numericRangeBody(filter: filterFunctions): NodeFunctionBody {
    const type = filter.infofilter.value
    const min = this.numericBound(filter, 0)
    const max = this.numericBound(filter, 1)

    const minBody = min === null ? null : this.numericCompareBody(filter, type, min, GREATER_OR_EQUAL_RULES)
    const maxBody = max === null ? null : this.numericCompareBody(filter, type, max, LESS_OR_EQUAL_RULES)

    if (minBody && maxBody) {
      const andBody = this.flowNode.getNodeFunctionBody([], null, "f", "xAND")
      andBody.ChildBodies.push(minBody, maxBody)
      return andBody
    }
    return minBody ?? maxBody
  }

  private numericBound(filter: filterFunctions, slot: 0 | 1): string | null {
    const raw = filter.svValues?.[slot]
    if (raw === null || raw === undefined) return null

    const value = String(raw).trim()
    if (value === "") return null

    const normalized = value.replace(/\./g, "").replace(",", ".")
    const parsed = Number(normalized)
    if (!Number.isFinite(parsed)) return null
    return this.isIntegerFilter(filter) ? String(Math.trunc(parsed)) : String(parsed)
  }

  private isIntegerFilter(filter: filterFunctions): boolean {
    return filter.infofilter.value === "Int32"
  }

  private numericCompareBody(filter: filterFunctions, type: string, value: string, ruleNames: string[]): NodeFunctionBody {
    const rule = this.ruleByDisplayName(ruleNames)
    const funcBody = this.flowNode.getNodeFunctionBody([], null, "f", rule?.functionType ?? filter.usedFunction?.functionType)
    const bodyData = this.dynamicVariablePath(filter)
    if (!bodyData) return funcBody
    funcBody.ChildBodies.push(this.flowNode.getDynamicVariable(bodyData))
    funcBody.ChildBodies.push(this.flowNode.getStaticVariable(type, value))
    return funcBody
  }

  private ruleByDisplayName(ruleNames: string[]): Arithmetic | undefined {
    for (const name of ruleNames) {
      const match = this.functionList.find((item) => item.displayName?.toLowerCase() === name)
      if (match) return match
    }
    return undefined
  }

  private EqualStatusCheck(filter: filterFunctions): NodeFunctionBody {
    const statuses = filter.reservationStatuses ?? []
    if (!statuses.length) return null

    const bodies = statuses.map((status) => this.statusCompareBody(status, filter.comparer?.value))
    if (bodies.length === 1) return bodies[0]

    const orBody = this.flowNode.getNodeFunctionBody([], null, "f", "xOR")
    bodies.forEach((body) => orBody.ChildBodies.push(body))
    return orBody
  }

  private statusCompareBody(status: LabelValue, comparer: string): NodeFunctionBody {
    var funcbody = this.flowNode.getNodeFunctionBody([], null, "f", "statusCompare")
    var funcBodyChildA = this.flowNode.getDynamicVariable(RESERVATION_STATUS_VALUE)
    var funcBodyChildB = this.flowNode.getStaticVariable("String", status.value)
    var funcBodyChildC = this.flowNode.getStaticVariable("String", comparer ?? DEFAULT_STATUS_COMPARER.value)
    funcbody.ChildBodies.push(funcBodyChildA)
    funcbody.ChildBodies.push(funcBodyChildB)
    funcbody.ChildBodies.push(funcBodyChildC)
    return funcbody
  }

  private DateTimeCheck(filter: filterFunctions): NodeFunctionBody {
    if (filter.comparer?.value === "Before" && filter.offSetDays <= 0) {
      return this.regularDateCheck(filter)
    } else if (filter.comparer?.value === "After" && filter.offSetDays >= 0) {
      return this.regularDateCheck(filter)
    }

    if (filter.comparer?.value === "After" && filter.offSetDays < 0) {
      return this.betweenDatesCheck(filter, "Offset")
    }
    if (filter.comparer?.value === "Before" && filter.offSetDays > 0) {
      return this.betweenDatesCheck(filter, "Date")
    }
    return null
  }

  private regularDateCheck(filter: filterFunctions): NodeFunctionBody {
    const funcbody = this.flowNode.getNodeFunctionBody([], null, "f", "dateCompare")
    const comparer = this.flowNode.getStaticVariable("String", filter.comparer.value === "Before" ? "<=" : ">=")
    funcbody.ChildBodies.push(this.currentHotelTime(), this.dateWithOffset(filter), comparer)
    return funcbody
  }

  private betweenDatesCheck(filter: filterFunctions, startingPoint: string): NodeFunctionBody {
    const funcbody = this.flowNode.getNodeFunctionBody([], null, "f", "dateBetween") // 1. Query, 2. From 3. To
    const dateFunctionDV = this.reservationDateVariable(filter)
    const dateAddedTimeSpan = this.dateWithOffset(filter)
    const fromDate = startingPoint === "Offset" ? dateAddedTimeSpan : dateFunctionDV
    const toDate = startingPoint === "Offset" ? dateFunctionDV : dateAddedTimeSpan
    funcbody.ChildBodies.push(this.currentHotelTime(), fromDate, toDate)
    return funcbody
  }

  private staticDateCheck(filter: filterFunctions): NodeFunctionBody {
    if (filter.dateStart === undefined && filter.dateEnd === undefined) {
      return null
    }
    // 1. Query, 2. Date to compare, 3. Comparer
    const funcbody = this.flowNode.getNodeFunctionBody([], null, "f", filter.dateStart ? "dateCompareAfter" : "dateCompareBefore")
    // Date start with time if time is included in the filter, otherwise just date
    const dateTime = filter.dateStart ? `${filter.dateStart}T${filter.timeSet?.from || "00:00"}:00` : `${filter.dateEnd}T${filter.timeSet?.to || "00:00"}:00`
    funcbody.ChildBodies.push(this.currentHotelTime(), this.flowNode.getStaticVariable("DateTime", dateTime), this.flowNode.getStaticVariable("String", "<="))
    return funcbody
  }

  private dateWithOffset(filter: filterFunctions): NodeFunctionBody {
    const body = this.flowNode.getNodeFunctionBody([], null, "f", "addTime")
    body.ChildBodies.push(this.reservationDateVariable(filter), this.flowNode.getStaticVariable("TimeSpan", `${filter.offSetDays}.00:00:00`))
    return body
  }

  private reservationDateVariable(filter: filterFunctions): NodeFunctionBody {
    return this.flowNode.getDynamicVariable(filter.infofilter.value === "StartDate" ? "Reservation:StartDate" : "Reservation:EndDate")
  }

  private currentHotelTime(): NodeFunctionBody {
    const body = this.flowNode.getNodeFunctionBody([], null, "f", "getTime")
    body.ChildBodies.push(this.flowNode.getDynamicVariable("SiteData:TimeZone"))
    return body
  }

  private weekTimeSpanFunction(filter: filterFunctions): NodeFunctionBody {
    if (filter.selectedWeekDays === undefined) return null
    const timeConfig = filter.visibilityDays?.timeConfig?.value
    const perDay = timeConfig === "DifferentHoursSelectedDays"
    if (!perDay && timeConfig !== "SameHoursSelectedDays") return this.flowNode.getNodeFunctionBody([], null, "f", "xOR")

    const xOrFunction = this.flowNode.getNodeFunctionBody([], null, "f", "xOR")
    filter.selectedWeekDays.forEach((day) => {
      const dayConfig = filter.visibilityDays[this.dayNameFromLabel(day)]
      const from = perDay ? dayConfig.from : filter.visibilityDays.AllDay ? "00:00" : filter.timeSet?.from || "00:00"
      const to = perDay ? dayConfig.to : filter.visibilityDays.AllDay ? "23:59" : filter.timeSet?.to || "23:59"
      xOrFunction.ChildBodies.push(this.withinTimeSpanBody(day, from, to))
    })
    return xOrFunction
  }

  private withinTimeSpanBody(day: string, from: string, to: string): NodeFunctionBody {
    const funcBody = this.flowNode.getNodeFunctionBody([], null, "f", "withinTimeSpan")
    funcBody.ChildBodies.push(this.flowNode.getDynamicVariable("SiteData:TimeZone"), this.flowNode.getStaticVariable("Int32", this.weekDays.indexOf(day)), this.flowNode.getStaticVariable("TimeOnly", `"${from}"`), this.flowNode.getStaticVariable("TimeOnly", `"${to}"`))
    return funcBody
  }
}
